"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { resolveDeliveryPlan } from "@shrimpster/ad-services-contracts";
import {
  GAM_RUNTIME_VERSION,
  createGamRuntime,
} from "@shrimpster/ad-services-publisher-web/gam";

const DISABLED_ADS = Object.freeze({
  enabled: false,
  plan: { kind: "none", reason: "ads-disabled" },
  runtime: null,
  environment: "",
  siteId: "",
  creativeTheme: null,
  consent: "unknown",
  requestPolicy: "strict",
  housePromotionEnabled: false,
  resolveHouseTargeting: async () => null,
});

const AdsContext = createContext(DISABLED_ADS);

export default function AdsProvider({
  enabled = false,
  placementId = "home.latest.inline",
  children,
}) {
  const [publicConfig, setPublicConfig] = useState(null);
  const runtimeRef = useRef(null);
  const houseSelectionPromisesRef = useRef(new Map());
  const houseSelectionControllersRef = useRef(new Map());
  const selectedHouseCandidatesRef = useRef(new Set());

  useEffect(() => {
    for (const controller of houseSelectionControllersRef.current.values()) controller.abort();
    houseSelectionPromisesRef.current.clear();
    houseSelectionControllersRef.current.clear();
    selectedHouseCandidatesRef.current.clear();
    if (!enabled) {
      setPublicConfig(null);
      runtimeRef.current?.disposeAll();
      runtimeRef.current = null;
      return undefined;
    }

    const controller = new AbortController();
    const query = new URLSearchParams({ placementId });
    fetch(`/api/ads/config?${query.toString()}`, {
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const payload = await response.json();
        return payload?.enabled ? payload : null;
      })
      .then((payload) => {
        if (!controller.signal.aborted) setPublicConfig(payload);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.warn("[ads] configuration unavailable", error);
        }
      });

    return () => controller.abort();
  }, [enabled, placementId]);

  const plan = useMemo(() => {
    if (!enabled || !publicConfig?.manifest || typeof window === "undefined") {
      return DISABLED_ADS.plan;
    }
    return resolveDeliveryPlan({
      manifest: publicConfig.manifest,
      hostname: window.location.hostname,
      runtimeVersion: GAM_RUNTIME_VERSION,
      siteId: publicConfig.siteId,
      placementId,
      formats: ["image"],
    });
  }, [enabled, placementId, publicConfig]);

  if (plan.kind === "provider-slot" && !runtimeRef.current) {
    runtimeRef.current = createGamRuntime();
  }

  useEffect(() => () => {
    runtimeRef.current?.disposeAll();
    runtimeRef.current = null;
    for (const controller of houseSelectionControllersRef.current.values()) controller.abort();
    houseSelectionControllersRef.current.clear();
  }, []);

  const resolveHouseTargeting = useCallback(async ({ opportunityId } = {}) => {
    if (!publicConfig?.housePromotion?.enabled || !opportunityId) return null;
    const existing = houseSelectionPromisesRef.current.get(opportunityId);
    if (existing) return existing;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 300);
    houseSelectionControllersRef.current.set(opportunityId, controller);
    const selectionPromise = fetch("/api/ads/house-selection", {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        placementId,
        opportunityId,
        excludedSpaceSlugs: [...selectedHouseCandidatesRef.current],
      }),
      signal: controller.signal,
    })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload?.eligible || !payload.spaceSlug) return null;
        selectedHouseCandidatesRef.current.add(payload.spaceSlug);
        return { candidateKey: "space_slug", candidateValue: payload.spaceSlug };
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.warn("[ads] House selection unavailable", error);
        }
        return null;
      })
      .finally(() => {
        clearTimeout(timer);
        houseSelectionControllersRef.current.delete(opportunityId);
      });
    houseSelectionPromisesRef.current.set(opportunityId, selectionPromise);
    return selectionPromise;
  }, [placementId, publicConfig?.housePromotion?.enabled]);

  const value = useMemo(() => ({
    enabled: plan.kind === "provider-slot",
    plan,
    runtime: runtimeRef.current,
    environment: publicConfig?.environment || "",
    siteId: publicConfig?.siteId || "",
    creativeTheme: publicConfig?.creativeTheme || null,
    consent: publicConfig?.consent || "unknown",
    requestPolicy: publicConfig?.requestPolicy || "strict",
    housePromotionEnabled: publicConfig?.housePromotion?.enabled === true,
    resolveHouseTargeting,
    sourceRevision: publicConfig?.sourceRevision || "",
  }), [plan, publicConfig, resolveHouseTargeting]);

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
}

export function useAds() {
  return useContext(AdsContext);
}
